import { useQueryClient } from '@tanstack/react-query';
import { type TransactionBase, type TransactionItemSubject, createTransactionDetail } from 'models';
import { useMutation } from 'query';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { getProfileQueryKey } from 'features/users/queries/keys';
import { createTransaction } from '../../services';

export type TransactionData = {
  items: Array<{
    amount: number;
    anonymous?: boolean;
    priceId: string;
    showTextContent?: boolean;
    subjectId: string;
    subjectType: TransactionItemSubject;
    textContent?: string;
  }>;
  payerInformation: {
    apt?: string;
    city: string;
    country: string;
    email: string;
    firstName: string;
    lastName: string;
    state?: string;
    street: string;
    zip: string;
  };
  recaptchaKey: string;
  updateProfile: boolean;
};

export const useCreateTransaction = () => {
  const queryClient = useQueryClient();
  return useMutation<TransactionBase, TransactionData>({
    async mutationFn({ items, payerInformation, recaptchaKey, updateProfile }) {
      const response = await createTransaction({
        /* eslint-disable @typescript-eslint/naming-convention -- Start: required by backend API */
        body: {
          items: items.map(({ amount, anonymous, priceId, showTextContent, subjectId, subjectType, textContent }) => ({
            amount,
            anonymous,
            price_id: priceId,
            show_text_content: showTextContent,
            subject_id: subjectId,
            subject_type: subjectType,
            text_content: textContent,
          })),
          payer: {
            apt: payerInformation.apt,
            city: payerInformation.city,
            country: payerInformation.country,
            email: payerInformation.email,
            first_name: payerInformation.firstName,
            last_name: payerInformation.lastName,
            state: payerInformation.state,
            street: payerInformation.street,
            zip: payerInformation.zip,
          },
          recaptcha_token: recaptchaKey,
          update_profile: updateProfile,
        },
        /* eslint-enable @typescript-eslint/naming-convention -- End: required by backend API */
      });

      return createTransactionDetail(response.data);
    },
    async onSuccess(_, { updateProfile }) {
      if (updateProfile) {
        await queryClient.invalidateQueries({ queryKey: getProfileQueryKey() });
      }
    },
  });
};
